import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Markdown from "@/components/Markdown";
import { Save, FileText, Users, Clock } from "lucide-react";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
}

export default function AdminLegal() {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [bumpVersion, setBumpVersion] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-legal-terms"],
    queryFn: async () => (await api.get("/admin/legal/terms")).data,
  });

  useEffect(() => {
    if (data?.document) {
      setTitle(data.document.title || "");
      setContent(data.document.content || "");
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/legal/terms", {
      title, content, bump_version: bumpVersion,
    })).data,
    onSuccess: () => {
      toast.success(bumpVersion
        ? "Documento actualizado. Se solicitará a todos los usuarios aceptar la nueva versión."
        : "Documento actualizado (versión sin cambio).");
      qc.invalidateQueries({ queryKey: ["admin-legal-terms"] });
      qc.invalidateQueries({ queryKey: ["legal-terms-public"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar"),
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const doc = data.document;
  const acceptedPct = data.total_users > 0
    ? Math.round((data.accepted_users / data.total_users) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="admin-legal-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Términos y Condiciones</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Edita el documento legal que los usuarios aceptan al registrarse. Al
          publicar una nueva versión, todos deberán volver a aceptar para
          continuar usando la app.
        </p>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <Stat label="Versión actual" value={`v${doc.version}`} icon={FileText} accent="blue" />
        <Stat label="Última actualización" value={fmtDate(doc.updated_at)} icon={Clock} accent="amber" />
        <Stat label="Editado por" value={doc.updated_by || "—"} icon={Users} accent="violet" />
        <Stat
          label="Aceptación"
          value={`${data.accepted_users}/${data.total_users} (${acceptedPct}%)`}
          icon={Users}
          accent="emerald"
        />
      </div>

      <Card className="border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Editor del documento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Título</label>
            <Input
              data-testid="admin-legal-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Términos y Condiciones"
            />
          </div>

          <Tabs defaultValue="edit">
            <TabsList>
              <TabsTrigger value="edit" data-testid="admin-legal-tab-edit">Editar (Markdown)</TabsTrigger>
              <TabsTrigger value="preview" data-testid="admin-legal-tab-preview">Vista previa</TabsTrigger>
            </TabsList>
            <TabsContent value="edit" className="mt-3">
              <Textarea
                data-testid="admin-legal-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={22}
                className="font-mono text-sm leading-relaxed resize-y min-h-[420px]"
                placeholder="# Términos y Condiciones..."
              />
              <p className="text-[11px] text-muted-foreground mt-2">
                Usa Markdown estándar: <code className="px-1">#</code> para títulos,
                <code className="px-1">**negrita**</code>,
                <code className="px-1">- listas</code>,
                <code className="px-1">[link](url)</code>.
              </p>
            </TabsContent>
            <TabsContent value="preview" className="mt-3">
              <div className="rounded-xl border border-white/40 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-5 max-h-[560px] overflow-y-auto kanban-scroll [&_h1]:font-display [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mb-2 [&_h2]:font-display [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-1 [&_p]:my-2 [&_p]:text-sm">
                <Markdown>{content}</Markdown>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
            <input
              id="bump-version"
              type="checkbox"
              data-testid="admin-legal-bump"
              checked={bumpVersion}
              onChange={(e) => setBumpVersion(e.target.checked)}
              className="mt-1 h-4 w-4"
            />
            <label htmlFor="bump-version" className="text-sm leading-relaxed cursor-pointer flex-1">
              <span className="font-medium">Publicar como nueva versión (v{doc.version + (bumpVersion ? 1 : 0)})</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Si está activado, los <b>{data.total_users}</b> usuarios deberán volver a
                aceptar los Términos para continuar usando la app. Desactívalo para
                guardar correcciones menores (ortografía, formato) sin re-solicitar
                aceptación.
              </p>
            </label>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-muted-foreground">
              {content.length.toLocaleString("es-MX")} caracteres ·{" "}
              {content.split(/\s+/).filter(Boolean).length.toLocaleString("es-MX")} palabras
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="admin-legal-save"
                  disabled={!content.trim() || save.isPending}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  {save.isPending ? "Guardando…" : (bumpVersion ? "Publicar nueva versión" : "Guardar cambios")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {bumpVersion ? "Publicar nueva versión" : "Guardar cambios"}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {bumpVersion
                      ? `Vas a publicar la versión v${doc.version + 1}. Todos los usuarios (${data.total_users}) verán el modal de aceptación la próxima vez que entren a la app. Esta acción no se puede deshacer.`
                      : "Vas a actualizar el contenido manteniendo la misma versión. Los usuarios ya aceptados no verán el modal de nuevo."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction data-testid="admin-legal-save-confirm" onClick={() => save.mutate()}>
                    Confirmar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  const ring = {
    blue:    "bg-blue-500/10 text-blue-600 dark:text-blue-300",
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
    amber:   "bg-amber-500/10 text-amber-600 dark:text-amber-300",
    violet:  "bg-violet-500/10 text-violet-600 dark:text-violet-300",
  }[accent];
  return (
    <div className="rounded-2xl border border-white/40 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl p-4 flex items-start gap-3">
      <div className={`h-9 w-9 rounded-xl grid place-items-center ${ring}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-semibold mt-0.5 truncate">{value}</div>
      </div>
    </div>
  );
}
